import { useEffect, useId, useRef, useState } from 'react';
import { LibraryIcon, RefreshCwIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmptyState } from '@/shared/components/EmptyState';
import { LibraryMark } from '@/shared/components/emptyMarks';
import { RetryButton } from '@/shared/components/RetryButton';
import type { LibraryItemView } from '@/lib/api/generated/model';
import { actionFor } from '../actions';
import { filterItems } from '../filterItems';
import { useInstallLibraryItem } from '../hooks/useInstallLibraryItem';
import { useInstallReport } from '../hooks/useInstallReport';
import { useLibraryItems } from '../hooks/useLibraryItems';
import { useUpdateAll } from '../hooks/useUpdateAll';
import { emptyNarrowingSentence, kindLabel } from '../labels';
import { clearInstallReport } from '../installReports';
import { isInWorkspace } from '../presence';
import {
  LIBRARY_TABS,
  itemRef,
  parseItemRef,
  type LibraryStateFilter,
  type LibraryTab,
} from '../searchParams';
import { libraryTotals } from '../totals';
import { LibraryItemDrawer } from './LibraryItemDrawer';
import { PendingRegion } from '@/shared/components/PendingRegion';
import { Skeleton } from '@/components/ui/skeleton';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { LibraryItemRow } from './LibraryItemRow';
import { LibraryMasthead } from './LibraryMasthead';
import { StateFilter } from './StateFilter';

/**
 * The Library: what can be installed into this workspace, and installing it.
 *
 * The page renders answers. Every state, refusal and dependency comes from the
 * service (spec 104, "the service decides, the page shows"); the only
 * judgement made here is which of its words to put on a row.
 */
export function LibraryPage({
  workspaceId,
  tab,
  q,
  state,
  item,
  onTabChange,
  onSearchChange,
  onStateChange,
  onItemChange,
}: {
  workspaceId: string;
  tab: LibraryTab;
  q: string;
  state: LibraryStateFilter;
  /** Which item's drawer is open, as the address writes it (spec 108 FR-001). */
  item: string;
  onTabChange: (tab: LibraryTab) => void;
  onSearchChange: (q: string) => void;
  onStateChange: (state: LibraryStateFilter) => void;
  /** Empty closes the drawer. */
  onItemChange: (item: string) => void;
}) {
  const { items, isPending, unavailable, refetch } =
    useLibraryItems(workspaceId);

  // A placeholder only once the wait is worth remarking on (spec 079 FR-017).
  const showPlaceholder = usePendingDisplay(isPending);
  const installer = useInstallLibraryItem(workspaceId);
  const updater = useUpdateAll(workspaceId);
  // Which item is open is not state here any more: it is in the address, so it
  // survives the reader following one of the install report's own links and
  // coming back (spec 108 US2). What the drawer shows is then derived from the
  // catalog, which also means a catalog that has not loaded yet simply has no
  // drawer, and one that loads with the item in it opens it.
  const selected = parseItemRef(item);
  const storedReport = useInstallReport(workspaceId, selected);
  const panelId = useId();
  const searchId = useId();
  // The address owns the search text, but a text field must keep up with the
  // keyboard between navigations: the draft is what is typed, the route is
  // what is shared. When the route moves (a back button, a shared link) the
  // draft follows it, adjusted during render as React recommends rather than
  // through an effect that would render twice.
  const [draft, setDraft] = useState(q);
  const [seenQ, setSeenQ] = useState(q);
  if (q !== seenQ) {
    setSeenQ(q);
    setDraft(q);
  }
  // Where focus goes back to when the drawer closes: the row that opened it.
  const openedFromRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // `/` puts the cursor in the search field, as it does on lists elsewhere.
  // Ignored while somebody is already typing, or holding a modifier, so it
  // never eats a slash somebody meant.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
      ) {
        return;
      }
      event.preventDefault();
      searchRef.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const { visible, counts } = filterItems(items, tab, q, state);
  // Over the whole catalog, not the filtered view: the tabs already count what
  // is filtered, and a fourth moving number would compete with them.
  const totals = libraryTotals(items);
  // What this workspace already holds, so a workflow's chips can say which of
  // its dependencies need no installing.
  const present = new Set(
    items
      .filter((i) => isInWorkspace(i.state))
      .map((i) => `${i.kind}/${i.key}`),
  );
  // Everything on screen that one press could bring up to date: a workflow is
  // never updated in place, and an item changed here is refused by the service,
  // so `actionFor` is the same rule the row's own button uses.
  const updatable = visible.filter((item) => actionFor(item) === 'update');
  // The other tabs that do have matches, for a search that found none here.
  const elsewhere = LIBRARY_TABS.filter(
    (kind) => kind !== tab && counts[kind] > 0,
  );
  // The drawer keeps showing the selected item as the catalog refreshes
  // underneath it, so its badge and dependencies stay current. An address
  // naming an item the catalog does not hold finds nothing here, which is the
  // whole of FR-002: no drawer, no error.
  const selectedNow =
    selected &&
    items.find((i) => i.kind === selected.kind && i.key === selected.key);

  // What the last install of the open item did. The mutation's own answer while
  // the reader is still here; the stored one after they have been away and come
  // back (spec 108 FR-006). `fresh` below is which of the two it is, and that is
  // knowable only here: the store cannot tell who wrote it.
  const report = installer.lastResult ?? storedReport;

  const open = (next: LibraryItemView, from: HTMLElement | null) => {
    // A second press on the row whose drawer is already open closes it, which
    // is what the press looks like it should do.
    if (selected?.kind === next.kind && selected.key === next.key) {
      onItemChange('');
      installer.reset();
      return;
    }
    openedFromRef.current = from;
    onItemChange(itemRef(next));
    installer.reset();
  };

  return (
    <section className="flex flex-1 flex-col gap-6 max-md:px-4 max-md:pt-4 p-6 pb-12">
      <LibraryMasthead
        q={draft}
        searchId={searchId}
        totals={items.length > 0 ? totals : undefined}
        inputRef={searchRef}
        onSearchChange={(next) => {
          setDraft(next);
          onSearchChange(next);
        }}
      />

      {isPending ? (
        showPlaceholder && (
          // Row-shaped placeholders, not the word "Loading". The catalog is a
          // list, and a sentence where a list will be moves everything below it
          // the moment the data lands. Three rows in the shape of the real ones
          // so nothing jumps.
          <PendingRegion label="Loading the library" className="gap-2">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                data-testid="library-row-skeleton"
                aria-hidden="true"
                className="flex items-start gap-4 px-4 py-4"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <Skeleton className="h-3.5 w-48 max-w-full rounded-sm" />
                  <Skeleton className="h-3 w-full max-w-[60ch] rounded-sm" />
                </div>
                <Skeleton className="h-8 w-20 shrink-0 rounded-md" />
              </div>
            ))}
          </PendingRegion>
        )
      ) : unavailable ? (
        <EmptyState
          icon={LibraryIcon}
          description="The library is unavailable right now. Nothing here has been lost; it just could not be read."
          action={<RetryButton onRetry={() => void refetch()} />}
        />
      ) : items.length === 0 ? (
        <EmptyState
          mark={<LibraryMark />}
          description="Nothing has been uploaded to the library yet. Agents, skills and workflows placed in the library's storage appear here."
          // Not a dead end: a reader who finds the catalog empty has no way to
          // know whether that is the truth or a stale read, and re-reading is
          // the one thing they can do about it.
          action={<RetryButton onRetry={() => void refetch()} />}
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            {/* Three tabs share a phone's width (docs/phone.md PH-03). */}
            <TabsList
              aria-label="Library kinds"
              className="max-md:w-full max-md:gap-0"
            >
              {LIBRARY_TABS.map((kind) => (
                <TabsTrigger
                  key={kind}
                  className="max-md:flex-1"
                  id={`${panelId}-tab-${kind}`}
                  panelId={panelId}
                  selected={tab === kind}
                  count={counts[kind]}
                  onClick={() => onTabChange(kind)}
                >
                  {kindLabel(kind, true)}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex items-center gap-2 max-md:w-full max-md:flex-wrap max-md:[&>button]:w-full">
              {state === 'updates' && updatable.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={updater.running}
                  onClick={() => {
                    updater.updateAll(updatable);
                  }}
                >
                  <RefreshCwIcon aria-hidden="true" />
                  {updater.running
                    ? `Updating ${String(updater.progress?.done ?? 0)} of ${String(updater.progress?.total ?? 0)}…`
                    : `Update all ${String(updatable.length)}`}
                </Button>
              )}
              <StateFilter value={state} onChange={onStateChange} />
            </div>
          </div>

          {updater.progress &&
            !updater.running &&
            updater.progress.refused.length > 0 && (
              <p role="status" className="text-destructive text-sm">
                {updater.progress.refused.length === 1
                  ? `${updater.progress.refused[0].key} was not updated: ${updater.progress.refused[0].reason}`
                  : `${String(updater.progress.refused.length)} items were not updated: ${updater.progress.refused.map((r) => r.key).join(', ')}`}
              </p>
            )}

          <div
            id={panelId}
            role="tabpanel"
            aria-labelledby={`${panelId}-tab-${tab}`}
          >
            {visible.length === 0 ? (
              /* A search that finds nothing on this tab has usually found
                 something on another one; saying only "no matches" leaves the
                 reader to guess that and to find the field again themselves. */
              <div className="text-muted-foreground flex flex-col items-center gap-3 py-10 text-center text-sm">
                <p>
                  {q.trim()
                    ? `No ${kindLabel(tab, true).toLowerCase()} match “${q.trim()}”.`
                    : emptyNarrowingSentence(
                        state === 'updates' ? 'updates' : 'installed',
                        tab,
                      )}
                </p>
                {elsewhere.length > 0 && (
                  <p className="flex flex-wrap items-center justify-center gap-x-1">
                    <span>Found in</span>
                    {elsewhere.map((kind, index) => (
                      <span key={kind}>
                        <Button
                          variant="link"
                          size="sm"
                          className="h-auto p-0"
                          onClick={() => onTabChange(kind)}
                        >
                          {kindLabel(kind, true)} ({counts[kind]})
                        </Button>
                        {index < elsewhere.length - 1 && ','}
                      </span>
                    ))}
                  </p>
                )}
                {q.trim() ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setDraft('');
                      onSearchChange('');
                    }}
                  >
                    Clear search
                  </Button>
                ) : (
                  state !== 'all' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        onStateChange('all');
                      }}
                    >
                      Show the whole library
                    </Button>
                  )
                )}
              </div>
            ) : (
              /* One grid for the whole list, so every row's columns are the
                 same width: a badge lines up with the badge above it and a
                 button with the button above it. Each row was its own grid
                 before, which is why nothing lined up. */
              <div className="border-border divide-border divide-y overflow-hidden rounded-lg border sm:grid sm:grid-cols-[minmax(0,1fr)_auto]">
                {visible.map((item) => (
                  <LibraryItemRow
                    key={`${item.kind}/${item.key}`}
                    item={item}
                    present={present}
                    items={items}
                    pending={installer.pending}
                    onSelect={(i, from) => open(i, from)}
                    onInstall={(i) => {
                      open(i, document.activeElement as HTMLElement | null);
                      installer.install(i.kind, i.key);
                    }}
                    onUpdate={(i) => {
                      open(i, document.activeElement as HTMLElement | null);
                      installer.update(i.kind, i.key);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <LibraryItemDrawer
        key={selected ? `${selected.kind}/${selected.key}` : 'none'}
        workspaceId={workspaceId}
        item={selectedNow}
        items={items}
        open={selectedNow !== undefined}
        onClose={() => {
          // Closing is the one clear signal that the receipt has been read, so
          // the report goes with the drawer (spec 108 FR-009).
          clearInstallReport(workspaceId, selected);
          onItemChange('');
          installer.reset();
          openedFromRef.current?.focus();
        }}
        installer={installer}
        report={report}
        fresh={installer.lastResult !== undefined}
        // Unlike a row, a link inside the drawer never closes it: it is always
        // somewhere else to go.
        onOpenItem={(next) => {
          onItemChange(itemRef(next));
          installer.reset();
        }}
      />
    </section>
  );
}
